// Session + in-progress application handle, kept in sessionStorage so a
// refresh does not lose the journey. No customer/application data is stored
// here — only the identifiers needed to call the API.
export interface Session {
  type: 'TRACKING' | 'FULL';
  reference?: string;
  applicationId?: string;
  customerId?: string;
  mobileNumber?: string;
  identifier?: string;
}

const SESSION_KEY = 'nova_session';
const APPLICATION_ID_KEY = 'nova_onboarding_application_id';
const ACCESS_TOKEN_KEY = 'nova_access_token';
const REFRESH_TOKEN_KEY = 'nova_refresh_token';

const readToken = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

export const getAccessToken = (): string | null => readToken(ACCESS_TOKEN_KEY);
export const getRefreshToken = (): string | null => readToken(REFRESH_TOKEN_KEY);
export const setTokens = (nextAccessToken?: string, nextRefreshToken?: string): void => {
  if (nextAccessToken !== undefined) sessionStorage.setItem(ACCESS_TOKEN_KEY, nextAccessToken);
  if (nextRefreshToken !== undefined) sessionStorage.setItem(REFRESH_TOKEN_KEY, nextRefreshToken);
};
export const clearTokens = (): void => {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
};

const read = <T>(key: string): T | null => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

// ---- Session ----
export const getSession = (): Session | null => read<Session>(SESSION_KEY);

export const setSession = (session: Session | null): void => {
  if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  else sessionStorage.removeItem(SESSION_KEY);
};

export const clearAuthSession = (): void => {
  clearTokens();
  localStorage.removeItem('customerId');
  setSession(null);
};

// ---- In-progress onboarding application id ----
export const getApplicationId = (): string | null =>
  sessionStorage.getItem(APPLICATION_ID_KEY);

export const setApplicationId = (id: string | null): void => {
  if (id) sessionStorage.setItem(APPLICATION_ID_KEY, id);
  else sessionStorage.removeItem(APPLICATION_ID_KEY);
};
